import { forwardRef, type CSSProperties, type InputHTMLAttributes, type ReactNode } from 'react'

/**
 * Shared UI primitives for the `/authentication/*` recovery surfaces.
 * Uses the `.ka-field` / `.ka-btn` / `.ka-auth-*` vocabulary from `ka.css`
 * so all auth surfaces share the same visual contract.
 */

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  readonly id: string
  readonly label: string
  readonly error?: string | undefined
}

export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { id, label, error, className, ...rest },
  ref,
) {
  const inputClassName = className
    ? `ka-field-input ${className}`
    : 'ka-field-input'
  return (
    <div className="ka-field">
      <label htmlFor={id} className="ka-field-label">
        {label}
      </label>
      <div className={`ka-field-input-wrap${error ? ' is-error' : ''}`}>
        <input
          id={id}
          ref={ref}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${id}-error` : undefined}
          className={inputClassName}
          {...rest}
        />
      </div>
      {error ? (
        <span id={`${id}-error`} className="ka-field-helper is-error">
          {error}
        </span>
      ) : null}
    </div>
  )
})

interface SubmitButtonProps {
  readonly loading: boolean
  readonly label: string
  readonly loadingLabel: string
  readonly disabled?: boolean
}

export function SubmitButton({
  loading,
  label,
  loadingLabel,
  disabled,
}: SubmitButtonProps): ReactNode {
  const isDisabled = loading || disabled === true
  return (
    <button
      type="submit"
      disabled={isDisabled}
      className="ka-btn ka-btn--primary ka-btn-block"
    >
      {loading ? loadingLabel : label}
    </button>
  )
}

export function ErrorBanner({
  message,
}: {
  readonly message: string
}): ReactNode {
  return (
    <div role="alert" className="ka-chip ka-chip--destructive">
      {message}
    </div>
  )
}

export function SuccessBanner({
  message,
}: {
  readonly message: string
}): ReactNode {
  return (
    <div role="status" className="ka-chip ka-chip--success">
      {message}
    </div>
  )
}

interface StatusBadgeProps {
  readonly tone: 'success' | 'destructive' | 'warning'
  readonly glyph: string
}

const TONE_CHIP_CLASS: Record<StatusBadgeProps['tone'], string> = {
  success: 'ka-chip--success',
  destructive: 'ka-chip--destructive',
  warning: 'ka-chip--warning',
}

export function StatusBadge({ tone, glyph }: StatusBadgeProps): ReactNode {
  return (
    <div
      aria-hidden="true"
      className={`ka-chip ${TONE_CHIP_CLASS[tone]} ka-auth-status-badge`}
    >
      {glyph}
    </div>
  )
}

export function Stack({
  children,
}: {
  readonly children: ReactNode
}): ReactNode {
  return <div className="ka-stack">{children}</div>
}

export function StatusCard({
  tone,
  glyph,
  title,
  body,
  children,
}: {
  readonly tone: StatusBadgeProps['tone']
  readonly glyph: string
  readonly title: string
  readonly body: ReactNode
  readonly children?: ReactNode
}): ReactNode {
  return (
    <div className="ka-auth-center-stack">
      <StatusBadge tone={tone} glyph={glyph} />
      <h1 className="ka-auth-h1 ka-auth-status-title">{title}</h1>
      <p className="ka-auth-sub ka-auth-status-body">{body}</p>
      {children}
    </div>
  )
}

// Legacy style-object exports kept for callers being migrated incrementally.
// Replace `style={heading}` → `className="ka-auth-h1"` and so on.
export const heading: CSSProperties = {}
export const subheading: CSSProperties = {}
export const helperText: CSSProperties = {}
export const linkButton: CSSProperties = {}
